import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Fab,
  Grid,
  LinearProgress,
  Stack,
  Toolbar,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BusinessIcon from "@mui/icons-material/Business";
import InsightsIcon from "@mui/icons-material/Insights";
import SecurityIcon from "@mui/icons-material/Security";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StarIcon from "@mui/icons-material/Star";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

const capabilities = [
  {
    label: "Discovery",
    title: "Government Scheme Discovery",
    description:
      "Explore central and state government schemes in one place with simplified, easy-to-understand information.",
    icon: <AutoAwesomeIcon />,
  },
  {
    label: "Tracking",
    title: "Search, Filter, and Understand",
    description:
      "Find schemes by category, eligibility, and benefits with structured filters and a user-friendly reading experience.",
    icon: <InsightsIcon />,
  },
  {
    label: "Languages",
    title: "Multi-language Access",
    description:
      "The app supports multiple Indian languages such as English, Hindi, and Marathi for wider accessibility.",
    icon: <SecurityIcon />,
  },
];

const screenshotPlaceholders = [
  {
    title: "Home and Discovery",
    caption: "Explore schemes with a clean home view and simplified navigation.",
    src: `${process.env.PUBLIC_URL}/Screenshot_20260423_183346.png`,
  },
  {
    title: "Scheme Details",
    caption: "Understand eligibility, benefits, and key information in one place.",
    src: `${process.env.PUBLIC_URL}/Screenshot_20260423_183404.png`,
  },
];

const quickStats = [
  { label: "Play Store Availability", value: "1K+ Downloads" },
  { label: "Category", value: "Education" },
  { label: "Content Rating", value: "Everyone" },
];

const testimonials = [
  {
    name: "Program Coordinator",
    text: "The structure makes it easy to understand schemes quickly and share details with beneficiaries.",
  },
  {
    name: "Field Volunteer",
    text: "The simplified information and filters save a lot of time during citizen support interactions.",
  },
  {
    name: "Support Team",
    text: "A clear and accessible app experience that helps us communicate eligibility and benefits better.",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeCapability, setActiveCapability] = useState(0);
  const [activeShot, setActiveShot] = useState(0);
  const [showTopButton, setShowTopButton] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveShot((prev) => (prev + 1) % screenshotPlaceholders.length);
    }, 3500);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      setShowTopButton(window.scrollY > 300);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const currentShot = screenshotPlaceholders[activeShot];
  const sliderProgress = useMemo(
    () => ((activeShot + 1) / screenshotPlaceholders.length) * 100,
    [activeShot]
  );
  const pageBg = isDark ? "#020617" : "#f8fafc";
  const cardBg = isDark ? "#0f172a" : "#ffffff";
  const textPrimary = isDark ? "#ffffff" : "#0f172a";
  const textSecondary = isDark ? alpha("#ffffff", 0.82) : "#64748b";
  const borderColor = isDark ? "#1e293b" : "#e2e8f0";
  const sectionReveal = (delay = 0) => ({
    opacity: 0,
    transform: "translateY(14px)",
    animation: `fadeSlide 0.65s ease forwards ${delay}s`,
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: pageBg,
        color: textPrimary,
        ...(isDark && {
          "& .MuiTypography-root": { color: "#ffffff" },
          "& .MuiTab-root": { color: alpha("#ffffff", 0.78) },
          "& .MuiTab-root.Mui-selected": { color: "#ffffff" },
          "& .MuiButton-outlined": {
            color: "#ffffff",
            borderColor: alpha("#ffffff", 0.45),
          },
          "& a": { color: "#93c5fd" },
        }),
        "@keyframes fadeSlide": {
          from: { opacity: 0, transform: "translateY(14px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={{ bgcolor: cardBg, color: textPrimary, borderBottom: `1px solid ${borderColor}` }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ justifyContent: "space-between", minHeight: 72 }}>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Avatar sx={{ bgcolor: "#1d4ed8", width: 34, height: 34 }}>S</Avatar>
              <Typography sx={{ fontWeight: 800 }}>Sahaj Yojana</Typography>
            </Stack>
            <Stack direction="row" spacing={1.2}>
              <Button
                size="small"
                onClick={() => window.scrollTo({ top: 620, behavior: "smooth" })}
                sx={{ textTransform: "none", color: textPrimary }}
              >
                Features
              </Button>
              <Button
                size="small"
                onClick={() => window.scrollTo({ top: 1450, behavior: "smooth" })}
                sx={{ textTransform: "none", color: textPrimary }}
              >
                Screenshots
              </Button>
              <Button
                size="small"
                component="a"
                href="https://play.google.com/store/apps/details?id=com.sahaj_yojna.app&hl=en_IN"
                target="_blank"
                rel="noreferrer"
                sx={{ textTransform: "none", color: textPrimary }}
              >
                Play Store
              </Button>
              <Button
                size="small"
                startIcon={isDark ? <LightModeIcon /> : <DarkModeIcon />}
                onClick={() => setIsDark((prev) => !prev)}
                sx={{ textTransform: "none", color: textPrimary }}
              >
                {isDark ? "Light" : "Dark"}
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Box
          sx={{
            ...sectionReveal(0),
            borderRadius: 4,
            p: { xs: 3, md: 6 },
            color: "#fff",
            background:
              "linear-gradient(135deg, #0f172a 0%, #1d4ed8 55%, #2563eb 100%)",
            boxShadow: "0 20px 50px rgba(37, 99, 235, 0.3)",
          }}
        >
          <Stack spacing={3} alignItems={{ xs: "flex-start", md: "center" }} textAlign={{ md: "center" }}>
            <Chip
              label="Sahaj Yojana Platform"
              sx={{ bgcolor: alpha("#ffffff", 0.18), color: "#fff", fontWeight: 600 }}
            />
            <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
              Stay informed about government schemes through one simple, trusted app experience.
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 840, color: alpha("#ffffff", 0.9) }}>
              Sahaj Yojana brings official scheme information into a clean, searchable interface so users can
              quickly understand eligibility, benefits, and available programs.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <Button
                variant="contained"
                size="large"
                component="a"
                href="https://play.google.com/store/apps/details?id=com.sahaj_yojna.app&hl=en_IN"
                target="_blank"
                rel="noreferrer"
                sx={{ px: 4, py: 1.4, fontWeight: 700, bgcolor: "#fff", color: "#1d4ed8" }}
              >
                Download on Play Store
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.scrollTo({ top: 700, behavior: "smooth" })}
                sx={{ px: 4, py: 1.4, fontWeight: 700, borderColor: "#fff", color: "#fff" }}
              >
                Explore Features
              </Button>
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          {quickStats.map((stat) => (
            <Grid item xs={12} md={4} key={stat.label}>
              <Card
                sx={{
                  ...sectionReveal(0.1),
                  bgcolor: cardBg,
                  borderRadius: 2.5,
                  transition: "transform 0.25s ease, box-shadow 0.25s ease",
                  "&:hover": { transform: "translateY(-4px)", boxShadow: 5 },
                }}
              >
                <CardContent>
                  <Typography variant="overline" sx={{ color: textSecondary }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Platform Capabilities
          </Typography>

          <Card sx={{ ...sectionReveal(0.2), borderRadius: 3, bgcolor: cardBg }}>
            <CardContent>
              <Tabs
                value={activeCapability}
                onChange={(_, newValue) => setActiveCapability(newValue)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  "& .MuiTab-root": { color: textSecondary, textTransform: "none", fontWeight: 600 },
                  "& .Mui-selected": { color: textPrimary },
                  "& .MuiTabs-indicator": { backgroundColor: "#2563eb" },
                }}
              >
                {capabilities.map((item) => (
                  <Tab key={item.label} label={item.label} />
                ))}
              </Tabs>
              <Box sx={{ mt: 2.5 }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }}>
                    {capabilities[activeCapability].icon}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {capabilities[activeCapability].title}
                  </Typography>
                </Stack>
                <Typography variant="body1" sx={{ color: textSecondary }}>
                  {capabilities[activeCapability].description}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={((activeCapability + 1) / capabilities.length) * 100}
                  sx={{ mt: 2.5, height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Grid container spacing={3} sx={{ mt: { xs: 2, md: 4 } }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ ...sectionReveal(0.3), borderRadius: 3, height: "100%", bgcolor: cardBg }}>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  App Screenshots
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
                  Real app screenshots from your mobile experience.
                </Typography>
                <Box
                  sx={{
                    height: { xs: 380, md: 520 },
                    borderRadius: 4,
                    border: `1px solid ${borderColor}`,
                    bgcolor: isDark ? "#0b1220" : "#eef2ff",
                    p: 1,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box
                    component="img"
                    src={currentShot.src}
                    alt={currentShot.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: 3,
                      bgcolor: "#fff",
                    }}
                  />
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {currentShot.title}
                </Typography>
                <Typography variant="body2" sx={{ color: textSecondary }}>
                  {currentShot.caption}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={sliderProgress}
                  sx={{ mt: 2.2, height: 7, borderRadius: 4 }}
                />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  {screenshotPlaceholders.map((_, idx) => (
                    <Box
                      key={`dot-${idx}`}
                      onClick={() => setActiveShot(idx)}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        cursor: "pointer",
                        bgcolor: idx === activeShot ? "#2563eb" : alpha(textSecondary, 0.5),
                        transition: "all 0.2s ease",
                      }}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card sx={{ ...sectionReveal(0.35), borderRadius: 3, height: "100%", bgcolor: cardBg }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Screenshot Thumbnails
                </Typography>
                <Stack spacing={1.2}>
                  {screenshotPlaceholders.map((shot, idx) => (
                    <Box
                      key={shot.title}
                      onClick={() => setActiveShot(idx)}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: `1px solid ${idx === activeShot ? "#2563eb" : borderColor}`,
                        bgcolor: idx === activeShot ? alpha("#2563eb", 0.15) : "transparent",
                        cursor: "pointer",
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={shot.src}
                        alt={shot.title}
                        sx={{
                          width: 58,
                          height: 96,
                          objectFit: "cover",
                          borderRadius: 1,
                          border: `1px solid ${borderColor}`,
                          flexShrink: 0,
                          bgcolor: "#fff",
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {shot.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: textSecondary }}>
                          {shot.caption}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Why Teams Choose Sahaj Yojana
          </Typography>
          <Grid container spacing={2}>
            {testimonials.map((item) => (
              <Grid item xs={12} md={4} key={item.name}>
                <Card
                  sx={{
                    ...sectionReveal(0.4),
                    bgcolor: cardBg,
                    borderRadius: 3,
                    height: "100%",
                    transition: "transform 0.25s ease, box-shadow 0.25s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 5 },
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={0.5} sx={{ mb: 1.3 }}>
                      <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                      <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                      <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                      <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                      <StarIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
                    </Stack>
                    <Typography variant="body2" sx={{ color: textSecondary, mb: 1.2 }}>
                      {item.text}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {item.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            About SAHAJYOJANA TECHNOLOGIES PRIVATE LIMITED
          </Typography>
        </Box>
        <Card sx={{ ...sectionReveal(0.5), borderRadius: 3, bgcolor: cardBg }}>
          <CardContent>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <Avatar sx={{ bgcolor: "#dbeafe", color: "#1d4ed8" }}>
                <BusinessIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Company Information
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              SAHAJYOJANA TECHNOLOGIES PRIVATE LIMITED
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary, mt: 1 }}>
              North Eye Villament Address: khanvilkar petrol pump, Nagalapark, Kolhapur, Maharashtra 416002
            </Typography>
          </CardContent>
        </Card>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Disclaimer and Information Sources
          </Typography>
          <Card sx={{ ...sectionReveal(0.55), borderRadius: 3, mb: 4, bgcolor: cardBg }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: textSecondary, mb: 1.5 }}>
                This app does not represent a government entity and is not affiliated with any government
                department, agency, or official service. Information is compiled from publicly available
                sources and presented for informational and educational purposes.
              </Typography>
              <Typography variant="body2" sx={{ color: textSecondary }}>
                Official references:
                {" "}
                <a href="https://www.myscheme.gov.in" target="_blank" rel="noreferrer">
                  myscheme.gov.in
                </a>
                {" "}
                and
                {" "}
                <a href="https://www.india.gov.in" target="_blank" rel="noreferrer">
                  india.gov.in
                </a>
                .
              </Typography>
            </CardContent>
          </Card>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Button
              variant="contained"
              component="a"
              href="https://play.google.com/store/apps/details?id=com.sahaj_yojna.app&hl=en_IN"
              target="_blank"
              rel="noreferrer"
            >
              View Official Play Store Listing
            </Button>
            <Button variant="outlined" onClick={() => navigate("/ads/login")}>
              Advertiser Login
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Frequently Asked Questions
          </Typography>
          <Accordion sx={{ bgcolor: cardBg, color: textPrimary }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: textPrimary }} />}>
              <Typography sx={{ fontWeight: 600 }}>Who should use this platform?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ color: textSecondary }}>
                Administrators and outreach teams can use Sahaj Yojana to manage scheme data,
                monitor requests, and streamline process execution.
              </Typography>
            </AccordionDetails>
          </Accordion>
          <Accordion sx={{ bgcolor: cardBg, color: textPrimary }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: textPrimary }} />}>
              <Typography sx={{ fontWeight: 600 }}>How do advertisers access the platform?</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography sx={{ color: textSecondary }}>
                Advertisers can use the dedicated button on this page to access the advertiser
                login flow directly.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>

        <Box
          sx={{
            mt: { xs: 5, md: 7 },
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            color: "#fff",
            background: "linear-gradient(135deg, #1e293b 0%, #1d4ed8 100%)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Ready to explore Sahaj Yojana?
            </Typography>
            <Typography sx={{ color: alpha("#ffffff", 0.9) }}>
              Check the Play Store listing or continue to advertiser login.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
            <Button
              variant="contained"
              component="a"
              href="https://play.google.com/store/apps/details?id=com.sahaj_yojna.app&hl=en_IN"
              target="_blank"
              rel="noreferrer"
              sx={{ bgcolor: "#fff", color: "#1d4ed8" }}
            >
              Open Play Store
            </Button>
            <Button variant="outlined" onClick={() => navigate("/ads/login")} sx={{ borderColor: "#fff", color: "#fff" }}>
              Advertiser Login
            </Button>
          </Stack>
        </Box>

        <Box sx={{ mt: 4, mb: 1, textAlign: "center", color: textSecondary }}>
          <Typography variant="body2">
            SAHAJYOJANA TECHNOLOGIES PRIVATE LIMITED - North Eye Villament Address: khanvilkar petrol pump,
            Nagalapark, Kolhapur, Maharashtra 416002
          </Typography>
        </Box>
      </Container>
      </Box>

      {showTopButton && (
        <Fab
          color="primary"
          size="medium"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          sx={{ position: "fixed", right: 20, bottom: 20 }}
          aria-label="Back to top"
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}
    </Box>
  );
};

export default LandingPage;
